import { supabase } from "@/integrations/supabase/client";

/** Deterministic 0-99 bucket from a stable id. */
function bucketFor(key: string, userId: string): number {
  let h = 2166136261;
  for (let i = 0; i < key.length; i++) h = Math.imul(h ^ key.charCodeAt(i), 16777619);
  for (let i = 0; i < userId.length; i++) h = Math.imul(h ^ userId.charCodeAt(i), 16777619);
  return Math.abs(h) % 100;
}

export interface FlagDecision { on: boolean; reason: string; bucket: number | null; }

/** Evaluate a feature flag for the current user and append an audit row. */
export async function evaluateFlag(flagKey: string): Promise<FlagDecision> {
  // Flag state is resolved through a narrow backend lookup: it returns only the
  // key, status and canary percentage. Full flag configuration (rollout notes,
  // targeting) stays restricted to founders/admins.
  const { data: rows } = await supabase.rpc("get_flag_state", { _flag_key: flagKey });
  const flag = Array.isArray(rows) ? rows[0] : null;
  const { data: { user } } = await supabase.auth.getUser();
  if (!flag) return { on: false, reason: "flag_missing", bucket: null };

  let decision: FlagDecision;
  if (flag.status === "on") decision = { on: true, reason: "status_on", bucket: null };
  else if (flag.status === "off") decision = { on: false, reason: "status_off", bucket: null };
  else {
    const uid = user?.id ?? "anon";
    const b = bucketFor(flagKey, uid);
    decision = { on: b < flag.canary_pct, reason: `canary:${flag.canary_pct}`, bucket: b };
  }

  if (user?.id) {
    await supabase.from("feature_flag_evaluations").insert({
      flag_key: flagKey, user_id: user.id, decision: decision.on, reason: decision.reason, bucket: decision.bucket,
    });
  }
  return decision;
}
